import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaHistory, FaDownload, FaChartLine, FaExclamationCircle } from 'react-icons/fa';
import './PaymentHistory.css';

function PaymentHistory({ companyId }) {
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      
      // Fetch payment history
      const historyResponse = await apiClient.get('/paywall/subscriptions/payment-history');
      setPayments(historyResponse.data);
      
      // Fetch payment summary
      const summaryResponse = await apiClient.get('/paywall/subscriptions/payment-summary');
      setPaymentSummary(summaryResponse.data);
      
    } catch (error) {
      console.error('Error fetching payment data:', error);
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
      case 'succeeded':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getPaymentMethodIcon = (type) => {
    if (type === 'card') return '💳';
    if (type === 'bank_transfer') return '🏦';
    return '💰';
  };

  if (loading) {
    return (
      <div className="payment-history-container">
        <div className="loading-spinner">Loading payment history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-history-container">
        <div className="error-message">
          <FaExclamationCircle /> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="payment-history-container">
      <div className="payment-history-header">
        <h3><FaHistory /> Payment History</h3>
        <div className="header-actions">
          <button 
            className="summary-toggle"
            onClick={() => setShowSummary(!showSummary)}
          >
            <FaChartLine /> {showSummary ? 'Hide' : 'Show'} Summary
          </button>
        </div>
      </div>

      {/* Payment Summary */}
      {showSummary && paymentSummary && (
        <div className="payment-summary">
          <div className="summary-card">
            <h4>Total Paid</h4>
            <p className="summary-value">{formatAmount(paymentSummary.total_paid)}</p>
          </div>
          <div className="summary-card">
            <h4>Successful Payments</h4>
            <p className="summary-value">{paymentSummary.successful_count}</p>
          </div>
          <div className="summary-card">
            <h4>Failed Payments</h4>
            <p className="summary-value">{paymentSummary.failed_count}</p>
          </div>
          <div className="summary-card">
            <h4>Total Refunded</h4>
            <p className="summary-value">{formatAmount(paymentSummary.total_refunded)}</p>
          </div>
        </div>
      )}

      {/* Payment Table */}
      {payments.length === 0 ? (
        <div className="no-payments">
          <p>No payment history available yet.</p>
        </div>
      ) : (
        <div className="payment-table">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.payment_date || payment.created_at)}</td>
                  <td>{payment.description || 'Subscription Payment'}</td>
                  <td className="amount-cell">
                    {formatAmount(payment.amount, payment.currency)}
                  </td>
                  <td>
                    {payment.payment_method && (
                      <div className="payment-method">
                        <span>{getPaymentMethodIcon(payment.payment_method.type)}</span>
                        <span>
                          {payment.payment_method.brand} ****{payment.payment_method.last4}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`payment-status ${getStatusColor(payment.status)}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td>
                    {payment.invoice_url && (
                      <a 
                        href={payment.invoice_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="invoice-link"
                      >
                        <FaDownload /> Download
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Payments from Summary */}
      {paymentSummary?.recent_payments && paymentSummary.recent_payments.length > 0 && (
        <div className="recent-payments">
          <h4>Quick Summary</h4>
          <p className="summary-text">
            You've made {paymentSummary.payment_count} payments totaling{' '}
            {formatAmount(paymentSummary.total_paid)}.
          </p>
        </div>
      )}
    </div>
  );
}

export default PaymentHistory;